/*
  Attar de Astana · About hero smoke

  A plume of oud smoke rising behind the headline, drawn as soft volume rather
  than as lines: every puff is a sprite, and where it sits is a pure function of
  time, so nothing accumulates between frames and any moment can be drawn on its
  own. The plume climbs into the frame on load and lifts away as the hero
  scrolls out.

  It does not answer the cursor. Smoke that chases a pointer reads as a toy, and
  the displacement it needs is what made the old strand version snap about.

  Reduced motion gets one still frame. With scripts off no canvas is added and
  the hero keeps the soft light its stylesheet paints where the smoke would rise.
*/

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const PAPER = '246, 244, 240';
  const SAGE = '120, 152, 150';
  const ORIGINS = { left: 0.26, center: 0.5, right: 0.74 };

  const PUFFS = 120;
  /* one lifetime for every puff, so they stay evenly spaced down the column
     however long the page is open. Variation goes into size and drift. */
  const LIFE = 13;
  const VARIANTS = 3;
  const SPRITE_PX = 160;
  const DPR_CAP = 1.25;
  const GROW_MS = 4200;
  const FADE_IN_MS = 1600;
  const CLOCK_START = 7;
  const STILL_AT = 19;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const smooth = (edge0, edge1, x) => {
    const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const hash = (n) => {
    const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
  };

  /* seeded, so the still frame under reduced motion is the same on every visit */
  const puffs = Array.from({ length: PUFFS }, (_, k) => ({
    /* births spread evenly down the column, so the plume is continuous */
    offset: k / PUFFS,
    /* a shade off the ladder, and a wobble either side of the shared path */
    lift: (hash(k + 101) - 0.5) * 0.009,
    jitter: hash(k + 201) - 0.5,
    size: 0.8 + hash(k + 501) * 0.5,
    tilt: hash(k + 601) * Math.PI * 2,
    spin: (hash(k + 701) - 0.5) * 0.6,
    alpha: 0.75 + hash(k + 801) * 0.5,
    variant: Math.floor(hash(k + 901) * VARIANTS) % VARIANTS,
    cool: hash(k + 1009) > 0.62,
  }));

  /*
    A puff is several offset radial gradients baked into one sprite, so it is
    never a clean disc. Baked once: the per-frame cost is one drawImage each.
  */
  const makeSprite = (seed, colour) => {
    const sprite = document.createElement('canvas');
    sprite.width = sprite.height = SPRITE_PX;
    const g = sprite.getContext('2d');
    for (let i = 0; i < 8; i++) {
      const r = (0.26 + hash(seed * 31 + i) * 0.22) * SPRITE_PX;
      const x = SPRITE_PX / 2 + (hash(seed * 37 + i + 11) - 0.5) * SPRITE_PX * 0.3;
      const y = SPRITE_PX / 2 + (hash(seed * 41 + i + 23) - 0.5) * SPRITE_PX * 0.3;
      const grad = g.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, `rgba(${colour}, 0.15)`);
      grad.addColorStop(0.35, `rgba(${colour}, 0.08)`);
      grad.addColorStop(0.7, `rgba(${colour}, 0.022)`);
      grad.addColorStop(1, `rgba(${colour}, 0)`);
      g.fillStyle = grad;
      g.fillRect(0, 0, SPRITE_PX, SPRITE_PX);
    }
    return sprite;
  };

  const warm = Array.from({ length: VARIANTS }, (_, i) => makeSprite(i + 1, PAPER));
  const cool = Array.from({ length: VARIANTS }, (_, i) => makeSprite(i + 7, SAGE));

  const createSmoke = (hero) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.className = 'about-hero__smoke';
    canvas.setAttribute('aria-hidden', 'true');
    const media = hero.querySelector(':scope > .about-hero__media');
    hero.insertBefore(canvas, media ? media.nextSibling : hero.firstChild);

    const origin = ORIGINS[hero.dataset.smokeOrigin] ?? ORIGINS.right;
    /* the plume leans back toward the middle of the frame as it climbs */
    const lean = origin > 0.6 ? -1 : origin < 0.4 ? 1 : 0;

    let w = 0;
    let h = 0;
    let dpr = 1;
    let scale = 1;
    let raf = 0;
    let last = 0;
    let clock = CLOCK_START;
    let inView = true;
    const born = performance.now();

    const resize = () => {
      w = hero.clientWidth;
      h = hero.clientHeight;
      dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      scale = clamp(Math.min(w, h * 1.5) / 1100, 0.5, 1.35);
    };

    const draw = (time, grow) => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      if (!w || !h) return;

      ctx.globalCompositeOperation = 'lighter';

      const sx = w * origin;
      const sy = h * 1.05;
      const reach = h * 1.16;
      const column = Math.min(w * 0.2, h * 0.24);
      const drift = lean * 0.1 * w;
      const base = h * 0.075 * scale;

      for (const puff of puffs) {
        const u = clamp(((time / LIFE + puff.offset) % 1) + puff.lift, 0, 1);

        /* in from the ember, out before the top: nothing appears or cuts */
        const fade = smooth(0, 0.14, u) * (1 - smooth(0.5, 1, u));
        /* on load the column fills from the bottom up */
        const tip = grow < 1 ? smooth(grow, grow - 0.22, u) : 1;
        const alpha = fade * tip * puff.alpha * 0.2;
        if (alpha < 0.004) continue;

        /*
          One path, shared. Every puff at a given height sits on it, so the
          column reads as one ribbon instead of separate specks. Subtracting
          time from the height phase sends the waves up with the smoke rather
          than holding them still in the frame.
        */
        const spread = Math.pow(u, 0.8);
        const q = u - time * 0.055;
        const wave = Math.sin(q * 7.5) * 0.55 + Math.sin(q * 13.3 + 1.7) * 0.26;
        const x =
          sx +
          wave * column * spread +
          puff.jitter * column * 0.16 * spread +
          drift * spread * spread;
        const y = sy - u * reach;
        const size = base * puff.size * (0.35 + 1.9 * spread);

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.translate(x, y);
        ctx.rotate(puff.tilt + puff.spin * (u * 1.8 + time * 0.04));
        ctx.globalAlpha = alpha;
        ctx.drawImage(
          puff.cool ? cool[puff.variant] : warm[puff.variant],
          -size * 0.62,
          -size * 0.5,
          size * 1.24,
          size
        );
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    const still = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      canvas.style.opacity = '1';
      canvas.style.transform = '';
      draw(STILL_AT, 1);
    };

    const tick = (now) => {
      raf = 0;
      const dt = last ? Math.min(now - last, 64) : 16.7;
      last = now;
      clock += dt / 1000;

      const age = now - born;
      const grow = 1 - Math.pow(1 - clamp(age / GROW_MS, 0, 1), 3);
      const rect = hero.getBoundingClientRect();
      const away = clamp(-rect.top / (rect.height * 0.75), 0, 1);

      canvas.style.opacity = ((1 - away) * smooth(0, FADE_IN_MS, age)).toFixed(3);
      canvas.style.transform = away > 0 ? `translate3d(0, ${(-away * 6).toFixed(2)}%, 0)` : '';

      if (away < 0.999) draw(clock, grow);
      schedule();
    };

    const schedule = () => {
      if (raf || !inView || reduced.matches || document.visibilityState !== 'visible') return;
      raf = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      last = 0;
      schedule();
    };

    const onMotion = () => {
      if (reduced.matches) still();
      else schedule();
    };

    const io = new IntersectionObserver((entries) => {
      inView = entries[entries.length - 1].isIntersecting;
      last = 0;
      schedule();
    });

    const ro = new ResizeObserver(() => {
      resize();
      if (reduced.matches) still();
      else schedule();
    });

    resize();
    io.observe(hero);
    ro.observe(hero);
    document.addEventListener('visibilitychange', onVisibility);
    reduced.addEventListener('change', onMotion);

    if (reduced.matches) still();
    else schedule();

    return {
      stop() {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        io.disconnect();
        ro.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        reduced.removeEventListener('change', onMotion);
        canvas.remove();
      },
    };
  };

  /* ---------------------------------------------------------------- wiring */

  const live = new Map();

  const init = (root) => {
    root.querySelectorAll('[data-about-hero][data-smoke]').forEach((hero) => {
      if (live.has(hero)) return;
      const smoke = createSmoke(hero);
      if (smoke) live.set(hero, smoke);
    });
  };

  const stopWithin = (root) => {
    live.forEach((smoke, hero) => {
      if (!root.contains(hero)) return;
      smoke.stop();
      live.delete(hero);
    });
  };

  init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => stopWithin(event.target));
})();
